import { useMemo, useState } from 'react';
import PageHeader from '@/components/base/PageHeader';
import StatusBadge from '@/components/base/StatusBadge';
import Badge from '@/components/base/Badge';
import EmptyState from '@/components/base/EmptyState';
import RowActions from '@/components/base/RowActions';
import { serviceStatuses, taskPriorities } from '@/types';
import { useData } from '@/context/DataContext';
import { formatTL, formatDate } from '@/lib/format';
import { useApp } from '@/context/AppContext';

export default function Service() {
  const { openCreate, openEdit, openDelete } = useApp();
  const { serviceRecords, customerName, projectName } = useData();
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('');
  const [priority, setPriority] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return serviceRecords.filter((s) => {
      const matchesQuery = !q || s.issue.toLowerCase().includes(q) || customerName(s.customerId).toLowerCase().includes(q) || projectName(s.projectId).toLowerCase().includes(q);
      const matchesStatus = !status || s.status === status;
      const matchesPriority = !priority || s.priority === priority;
      return matchesQuery && matchesStatus && matchesPriority;
    });
  }, [query, status, priority, serviceRecords, customerName, projectName]);

  return (
    <div className="space-y-5 animate-fade-in-up">
      <PageHeader
        title="Servis / Revizyon"
        description={`${filtered.length} servis kaydı`}
        actions={
          <button type="button" onClick={() => openCreate('service')} className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-600">
            <i className="ri-add-line text-lg" /> Yeni Servis Kaydı
          </button>
        }
      />

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex min-w-[220px] flex-1 items-center gap-2 rounded-md border border-background-300 bg-background-50 px-3">
          <i className="ri-search-line text-base text-foreground-400" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Sorun, müşteri, proje ara..." className="h-9 w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 outline-none" />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-9 cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-700">
          <option value="">Tüm Durumlar</option>
          {serviceStatuses.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={priority} onChange={(e) => setPriority(e.target.value)} className="h-9 cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 text-sm text-foreground-700">
          <option value="">Tüm Öncelikler</option>
          {taskPriorities.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
      </div>

      {filtered.length === 0 ? (
        <EmptyState title="Servis kaydı bulunamadı" description="Filtreleri değiştirerek tekrar deneyin." />
      ) : (
        <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1000px] text-sm">
              <thead>
                <tr className="border-b border-background-200 bg-background-100/50 text-left text-xs font-semibold uppercase tracking-wide text-foreground-500">
                  <th className="px-5 py-3">Müşteri</th>
                  <th className="px-4 py-3">Makine / Proje</th>
                  <th className="px-4 py-3">Talep</th>
                  <th className="px-4 py-3">Sorun</th>
                  <th className="px-4 py-3">Öncelik</th>
                  <th className="px-4 py-3">Sorumlu</th>
                  <th className="px-4 py-3">Planlanan</th>
                  <th className="px-4 py-3">Garanti</th>
                  <th className="px-4 py-3 text-right">Bedel</th>
                  <th className="px-4 py-3">Durum</th>
                  <th className="px-4 py-3 text-right">İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((s) => (
                  <tr key={s.id} className="border-b border-background-100 transition-colors last:border-0 hover:bg-background-100/60">
                    <td className="px-5 py-3.5 font-semibold text-foreground-900">{customerName(s.customerId)}</td>
                    <td className="px-4 py-3.5 text-foreground-700">{projectName(s.projectId)}</td>
                    <td className="px-4 py-3.5 text-foreground-600">{formatDate(s.requestDate)}</td>
                    <td className="px-4 py-3.5 text-foreground-800">{s.issue}</td>
                    <td className="px-4 py-3.5"><Badge tone={s.priority === 'Kritik' ? 'accentSolid' : s.priority === 'Önemli' ? 'accent' : 'neutral'}>{s.priority}</Badge></td>
                    <td className="px-4 py-3.5 text-foreground-700">{s.assignee}</td>
                    <td className="px-4 py-3.5 text-foreground-600">{formatDate(s.plannedDate)}</td>
                    <td className="px-4 py-3.5"><StatusBadge status={s.warranty} /></td>
                    <td className="px-4 py-3.5 text-right font-semibold text-foreground-900">{s.fee > 0 ? formatTL(s.fee) : '-'}</td>
                    <td className="px-4 py-3.5"><StatusBadge status={s.status} /></td>
                    <td className="px-4 py-3.5"><RowActions onEdit={() => openEdit('service', s)} onDelete={() => openDelete('service', s.id, s.issue)} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}